import axios, {
  type AxiosError,
  type AxiosAdapter,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'
import type { Envelope, TokenPair } from '@/types/api.types'
import { appLocale } from '@/i18n/locale.store'
import { trackResponse } from '@/lib/analytics'

/** Unwraps the envelope; on 401 refreshes once, sharing one in-flight refresh between concurrent requests. `createApi` lets tests inject a token source and adapter. */

export interface TokenSource {
  getAccessToken(): string | null
  getRefreshToken(): string | null
  setTokens(tokens: TokenPair): void
  clear(): void
}

export interface ApiOptions {
  baseUrl: string
  tokens: TokenSource
  onSessionExpired?: () => void
  adapter?: AxiosAdapter
  timeoutMs?: number
  /** Client identity headers (LP-942), added to every request and to the refresh call. */
  clientHeaders?: () => Record<string, string>
}

export class ApiError extends Error {
  readonly status: number
  readonly cause?: unknown
  /** The envelope's `data` on a rejection, when the backend sent one — a 409's `nextAllowedAt`, for example. */
  readonly data?: unknown
  constructor(status: number, message: string, cause?: unknown, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.cause = cause
    this.data = data
  }
  /** Server unreachable, DNS failure or the request timed out — not a rejection by the API. */
  get isNetwork(): boolean {
    return this.status === 0 || this.status === 408 || this.status === 503
  }
}

export interface RequestOptions extends AxiosRequestConfig {
  /** Public endpoint: do not attach the bearer token and never try to refresh on 401. */
  skipAuth?: boolean
}

type RetryableConfig = InternalAxiosRequestConfig & { skipAuth?: boolean; _retried?: boolean }

export const REFRESH_PATH = '/auth/token/refresh'

/** Same headers as leazepro_app's NetworkCall.getDefaultHeader. */
export function timezoneHeaders(now: Date = new Date()): Record<string, string> {
  const offsetSeconds = -now.getTimezoneOffset() * 60
  let abbreviation = 'UTC'
  try {
    const locale = typeof navigator === 'undefined' ? 'en-IN' : navigator.language
    abbreviation =
      new Intl.DateTimeFormat(locale, { timeZoneName: 'short' })
        .formatToParts(now)
        .find((p) => p.type === 'timeZoneName')?.value ?? 'UTC'
  } catch {
    // Intl unavailable: the offset still tells the backend everything it needs.
  }
  return { 'Timezone-Abbreviation': abbreviation, 'Timezone-Offset-Seconds': String(offsetSeconds), 'Accept-Language': appLocale() }
}

function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error
  if (axios.isAxiosError(error)) {
    const envelope = error.response?.data as Partial<Envelope> | undefined
    if (error.response) {
      return new ApiError(error.response.status, envelope?.message || error.message, error, envelope?.data)
    }
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return new ApiError(408, 'Request timed out. Please try again.', error)
    }
    return new ApiError(503, 'Connection failed. Please check your internet connection.', error)
  }
  return new ApiError(500, error instanceof Error ? error.message : 'Something went wrong', error)
}

export interface Api {
  instance: AxiosInstance
  get<T>(url: string, options?: RequestOptions): Promise<T>
  post<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T>
  put<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T>
  patch<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T>
  delete<T>(url: string, options?: RequestOptions): Promise<T>
}

export function createApi(options: ApiOptions): Api {
  const { baseUrl, tokens, onSessionExpired, adapter, timeoutMs = 30_000, clientHeaders } = options
  const requestHeaders = () => ({ ...timezoneHeaders(), ...clientHeaders?.() })

  const instance = axios.create({
    baseURL: baseUrl,
    timeout: timeoutMs,
    headers: { 'Content-Type': 'application/json' },
    ...(adapter ? { adapter } : {}),
  })

  // Raw client for the refresh call itself: no interceptors, so a 401 here cannot recurse.
  const bare = axios.create({ baseURL: baseUrl, timeout: timeoutMs, ...(adapter ? { adapter } : {}) })

  let inFlightRefresh: Promise<string> | null = null

  async function refreshTokens(): Promise<string> {
    const refreshToken = tokens.getRefreshToken()
    if (!refreshToken) throw new ApiError(401, 'Session expired')
    try {
      const { data } = await bare.post<Envelope<TokenPair>>(REFRESH_PATH, { refreshToken }, { headers: requestHeaders() })
      tokens.setTokens(data.data)
      return data.data.accessToken
    } catch (error) {
      const apiError = toApiError(error)
      // Only a verdict on the refresh token ends the session: 401 (invalid, expired, revoked) or 403
      // (malformed). A 5xx, 408, 429 or a dropped connection keeps the pair so the next 401 tries again.
      if (apiError.status === 401 || apiError.status === 403) {
        tokens.clear()
        onSessionExpired?.()
        throw new ApiError(401, 'Session expired', apiError)
      }
      throw apiError
    }
  }

  /** Concurrent 401s wait on the same refresh; the promise is dropped once settled so a later 401 can refresh again. */
  function refreshOnce(): Promise<string> {
    if (!inFlightRefresh) {
      inFlightRefresh = refreshTokens().finally(() => {
        inFlightRefresh = null
      })
    }
    return inFlightRefresh
  }

  instance.interceptors.request.use((config) => {
    const cfg = config as RetryableConfig
    Object.entries(requestHeaders()).forEach(([k, v]) => cfg.headers.set(k, v))
    const accessToken = tokens.getAccessToken()
    if (!cfg.skipAuth && accessToken) cfg.headers.set('Authorization', `Bearer ${accessToken}`)
    return cfg
  })

  instance.interceptors.response.use(
    (response) => {
      trackResponse(response.config.method, response.config.url, response.config.data, (response.data as Partial<Envelope> | undefined)?.data)
      return response
    },
    async (error: AxiosError) => {
      const cfg = error.config as RetryableConfig | undefined
      const status = error.response?.status
      // A retried request is never refreshed twice. The wrong role is a 403 from the backend (LP-794).
      if (status !== 401 || !cfg || cfg.skipAuth || cfg._retried) {
        throw toApiError(error)
      }
      if (!tokens.getRefreshToken()) {
        tokens.clear()
        onSessionExpired?.()
        throw toApiError(error)
      }
      cfg._retried = true
      const accessToken = await refreshOnce()
      cfg.headers.set('Authorization', `Bearer ${accessToken}`)
      return instance.request(cfg)
    },
  )

  const unwrap = async <T>(request: Promise<{ data: Envelope<T> }>): Promise<T> => {
    try {
      const { data } = await request
      return data.data
    } catch (error) {
      throw toApiError(error)
    }
  }

  return {
    instance,
    get: (url, options) => unwrap(instance.get(url, options)),
    post: (url, body, options) => unwrap(instance.post(url, body, options)),
    put: (url, body, options) => unwrap(instance.put(url, body, options)),
    patch: (url, body, options) => unwrap(instance.patch(url, body, options)),
    delete: (url, options) => unwrap(instance.delete(url, options)),
  }
}
